import { useState, useEffect } from 'react';
import { Star, TrendingUp, Clock, MapPin } from 'lucide-react';
import { supabase } from '../lib/supabase';

interface SocialProofProps {
  variant?: 'testimonials' | 'signups' | 'combined';
  showAnimation?: boolean;
}

interface RecentSignup {
  id: string;
  display_name: string;
  signed_up_at: string;
  location?: string;
}

export default function SocialProof({ variant = 'combined', showAnimation = true }: SocialProofProps) {
  const [signupsToday, setSignupsToday] = useState(12);
  const [recentSignups, setRecentSignups] = useState<RecentSignup[]>([]);
  const [currentTestimonialIndex, setCurrentTestimonialIndex] = useState(0);
  const [showNotification, setShowNotification] = useState(false);

  const testimonials = [
    {
      name: 'Lisa M.',
      rating: 5,
      text: 'In 7 Tagen -4,8 kg! Ich fühle mich so viel besser und habe endlich wieder Energie.',
      verified: true,
    },
    {
      name: 'Sarah K.',
      rating: 5,
      text: 'Unglaublich! Meine Hosen passen wieder und ich schlafe so viel besser.',
      verified: true,
    },
    {
      name: 'Julia S.',
      rating: 5,
      text: 'Das Programm hat mein Leben verändert. -5,2 kg und ich fühle mich wie neu geboren!',
      verified: true,
    },
  ];

  useEffect(() => {
    fetchSocialProofData();

    const channel = supabase
      .channel('social_proof_changes')
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'spot_counter' },
        (payload) => {
          if (payload.new && typeof payload.new === 'object' && 'signups_today' in payload.new) {
            setSignupsToday(payload.new.signups_today as number);
          }
        }
      )
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'recent_signups' },
        () => {
          fetchRecentSignups();
          if (showAnimation) {
            setShowNotification(true);
            setTimeout(() => setShowNotification(false), 5000);
          }
        }
      )
      .subscribe();

    const testimonialInterval = setInterval(() => {
      setCurrentTestimonialIndex((prev) => (prev + 1) % testimonials.length);
    }, 5000);

    return () => {
      supabase.removeChannel(channel);
      clearInterval(testimonialInterval);
    };
  }, [showAnimation, testimonials.length]);

  const fetchSocialProofData = async () => {
    try {
      const { data: spotData } = await supabase
        .from('spot_counter')
        .select('signups_today')
        .maybeSingle();

      if (spotData) {
        setSignupsToday(spotData.signups_today);
      }

      await fetchRecentSignups();
    } catch (error) {
      console.error('Error fetching social proof data:', error);
    }
  };

  const fetchRecentSignups = async () => {
    try {
      const { data } = await supabase
        .from('recent_signups')
        .select('*')
        .order('signed_up_at', { ascending: false })
        .limit(5);

      if (data) {
        setRecentSignups(data);
      }
    } catch (error) {
      console.error('Error fetching recent signups:', error);
    }
  };

  const getTimeAgo = (timestamp: string) => {
    const now = new Date();
    const signupTime = new Date(timestamp);
    const diffMs = now.getTime() - signupTime.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);

    if (diffMins < 60) {
      return `vor ${diffMins} Min.`;
    } else if (diffHours < 24) {
      return `vor ${diffHours} Std.`;
    } else {
      return 'heute';
    }
  };

  if (variant === 'signups') {
    return (
      <div className="space-y-3">
        <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg p-4 border border-green-200">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-green-600 rounded-full flex items-center justify-center">
              <TrendingUp className="w-6 h-6 text-white" />
            </div>
            <div>
              <div className="text-2xl font-bold text-gray-900">{signupsToday}</div>
              <div className="text-sm text-gray-600">Anmeldungen heute</div>
            </div>
          </div>
        </div>

        {showNotification && recentSignups.length > 0 && (
          <div className="bg-white rounded-lg p-3 shadow-lg border-l-4 border-green-500 animate-slideInRight">
            <div className="flex items-start gap-2">
              <Clock className="w-4 h-4 text-green-600 mt-1" />
              <div className="flex-1">
                <p className="text-sm font-semibold text-gray-900">
                  {recentSignups[0].display_name} hat sich gerade angemeldet
                </p>
                {recentSignups[0].location && (
                  <p className="text-xs text-gray-500 flex items-center gap-1 mt-1">
                    <MapPin className="w-3 h-3" />
                    {recentSignups[0].location}
                  </p>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  if (variant === 'testimonials') {
    const currentTestimonial = testimonials[currentTestimonialIndex];
    return (
      <div className="bg-white rounded-lg p-4 shadow-md border border-gray-200">
        <div className="flex items-center gap-1 mb-2">
          {[...Array(currentTestimonial.rating)].map((_, i) => (
            <Star key={i} className="w-4 h-4 fill-amber-400 text-amber-400" />
          ))}
        </div>
        <p className="text-sm text-gray-700 italic mb-3">"{currentTestimonial.text}"</p>
        <div className="flex items-center justify-between">
          <span className="text-sm font-semibold text-gray-900">{currentTestimonial.name}</span>
          {currentTestimonial.verified && (
            <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded-full font-medium">
              Verifiziert ✓
            </span>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="grid sm:grid-cols-2 gap-4">
        <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl p-4 border-2 border-green-200 shadow-md">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 bg-green-600 rounded-full flex items-center justify-center">
              <TrendingUp className="w-5 h-5 text-white" />
            </div>
            <div>
              <div className="text-2xl font-bold text-gray-900">{signupsToday}</div>
              <div className="text-xs text-gray-600">Heute angemeldet</div>
            </div>
          </div>
          <div className="flex items-center gap-1 text-xs text-green-700">
            <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
            <span>Andere sichern sich gerade ihren Platz</span>
          </div>
        </div>

        <div className="bg-white rounded-xl p-4 border-2 border-amber-200 shadow-md">
          <div className="flex items-center gap-1 mb-2">
            {[...Array(5)].map((_, i) => (
              <Star key={i} className="w-4 h-4 fill-amber-400 text-amber-400" />
            ))}
          </div>
          <p className="text-xs text-gray-700 italic mb-2">
            "{testimonials[currentTestimonialIndex].text}"
          </p>
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold text-gray-900">
              {testimonials[currentTestimonialIndex].name}
            </span>
            <span className="text-[10px] bg-green-100 text-green-700 px-2 py-1 rounded-full font-medium">
              Verifiziert ✓
            </span>
          </div>
        </div>
      </div>

      {recentSignups.length > 0 && (
        <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
          <div className="text-xs font-semibold text-gray-700 mb-2 flex items-center gap-2">
            <Clock className="w-3 h-3" />
            Letzte Anmeldungen:
          </div>
          <div className="space-y-1">
            {recentSignups.slice(0, 3).map((signup) => (
              <div key={signup.id} className="text-xs text-gray-600 flex items-center justify-between">
                <span className="font-medium">{signup.display_name}</span>
                <span className="text-gray-500">{getTimeAgo(signup.signed_up_at)}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
